import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  User,
  UserCircle2,
  Wallet,
  Settings,
  LogOut,
  FileText,
  ClipboardList,
  Menu,
  X,
  MessageSquare,
  HelpCircle,
  FolderKanban,
} from 'lucide-react';
import { userService } from '../../services/userService';
import { timService } from '../../services/timService';
import { NotificationBell } from '../UI/NotificationBell';
import { UserProfile } from '../../types/api';
import Publisher from '../Profile/shared/Publisher';
import { DiscoverMegaMenu } from './DiscoverMegaMenu';
import { MessageHoverMenu } from '../UI/MessageHoverMenu';
import { buildOwnProfilePath } from '../../utils/share';
import { OFFICIAL_STUDIO_PROFILE_PATH } from '../../constants/official';
import { PM_BASE_URL } from '../../constants/sites';

interface HeaderProps {
  onRequireAuth: () => void;
}

const Header: React.FC<HeaderProps> = ({ onRequireAuth }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState<UserProfile | null>(null);
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const useDarkSurfaceHeader = false;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    let isActive = true;
    let unsubscribeConversationUpdated: (() => void) | null = null;

    const syncSessionState = async () => {
      const token = localStorage.getItem('token');
      if (!isActive) {
        return;
      }

      setIsLoggedIn(!!token);

      if (!token) {
        unsubscribeConversationUpdated?.();
        unsubscribeConversationUpdated = null;
        setCurrentUserProfile(null);
        setUnreadMsgCount(0);
        return;
      }

      try {
        const response = await userService.getUserProfile();
        if (!isActive || !response.success || !response.data?.userProfile) {
          return;
        }

        setCurrentUserProfile(response.data.userProfile);

        await timService.login(response.data.userProfile.userId);

        if (!isActive) {
          return;
        }

        unsubscribeConversationUpdated?.();

        const updateUnread = async () => {
          const convs = await timService.getConversationList();
          const total = convs.reduce((sum: number, c: any) => sum + (c.unreadCount || 0), 0);
          if (isActive) {
            // The message icon pulses continuously while this is > 0
            // (see MessageHoverMenu / the mobile icon), and stops on read.
            setUnreadMsgCount(total);
          }
        };

        await updateUnread();
        unsubscribeConversationUpdated = timService.onConversationUpdated(updateUnread);
      } catch (error) {
        console.error('Failed to fetch user profile for header:', error);
      }
    };

    const handleSessionChange = () => {
      void syncSessionState();
    };

    void syncSessionState();

    window.addEventListener('auth-change', handleSessionChange);
    window.addEventListener('storage', handleSessionChange);
    window.addEventListener('profile-updated', handleSessionChange);

    return () => {
      isActive = false;
      unsubscribeConversationUpdated?.();
      window.removeEventListener('auth-change', handleSessionChange);
      window.removeEventListener('storage', handleSessionChange);
      window.removeEventListener('profile-updated', handleSessionChange);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  // Close any open dropdowns when the viewport crosses the md breakpoint
  // (e.g. user rotates a tablet or DevTools shrinks the window) to avoid
  // a hover-only desktop dropdown lingering after we've switched to the
  // tap-driven mobile UI.
  useEffect(() => {
    const handleResize = () => {
      if (window.matchMedia('(min-width: 768px)').matches) {
        setShowMobileMenu(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const userInfo = useMemo(() => {
    if (!currentUserProfile) return undefined;
    return {
      id: currentUserProfile.userId,
      name: currentUserProfile.displayName || currentUserProfile.username || currentUserProfile.userId,
      avatar: currentUserProfile.avatarUrl || ''
    };
  }, [currentUserProfile]);

  const handleLogout = () => {
    timService.logout().catch(console.error);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setCurrentUserProfile(null);
    setUnreadMsgCount(0);
    window.dispatchEvent(new Event('auth-change'));
    setIsLoggedIn(false);
    setShowUserMenu(false);
    navigate('/');
  };

  const ownProfilePath = currentUserProfile?.userId
    ? buildOwnProfilePath(currentUserProfile.userId)
    : '/profile';

  const menuItems = [
    { icon: UserCircle2, label: '个人主页', path: ownProfilePath },
    { icon: FolderKanban, label: '项目管理', path: '/contracts' },
    { icon: FileText, label: '我的合同', path: '/my-contracts' },
    { icon: ClipboardList, label: '我的试稿', path: '/trials' },
    { icon: Wallet, label: '交易记录', path: '/transactions' },
    { icon: Settings, label: '通用设置', path: '/settings' },
  ];

  const avatarUrl = currentUserProfile?.avatarUrl || null;

  return (
    <>
      <nav className={`fixed top-0 z-50 w-full transition-all duration-300 ${isScrolled ? 'bg-white/90 py-3 shadow-sm backdrop-blur-md' : 'bg-transparent py-4'}`}>
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 md:px-12">
          <Link to="/" className={`text-xl font-medium tracking-tight transition-colors ${useDarkSurfaceHeader ? 'text-white' : 'text-gray-900'}`}>CraftCloud 云作</Link>
          <div className={`hidden items-center gap-8 text-sm font-medium transition-colors md:flex h-full ${useDarkSurfaceHeader ? 'text-white/80' : 'text-gray-600'}`}>
            <div className="group relative py-6" data-tour="find-creators">
                <Link to="/discover?tab=creators" className={`transition-colors ${useDarkSurfaceHeader ? 'hover:text-white' : 'hover:text-black'}`}>找创作者</Link>
                <DiscoverMegaMenu mode="creators" />
            </div>
            <div className="group relative py-6" data-tour="browse-briefs">
                <Link to="/discover?tab=briefs" className={`transition-colors ${useDarkSurfaceHeader ? 'hover:text-white' : 'hover:text-black'}`}>浏览需求</Link>
                <DiscoverMegaMenu mode="briefs" />
            </div>
            <div className="py-6">
                <Link to="/about" className={`transition-colors ${useDarkSurfaceHeader ? 'hover:text-white' : 'hover:text-black'}`}>关于我们</Link>
            </div>
            <div className="py-6">
                <Link to={OFFICIAL_STUDIO_PROFILE_PATH} className={`transition-colors ${useDarkSurfaceHeader ? 'hover:text-white' : 'hover:text-black'}`}>云作Studio</Link>
            </div>
            <div className="py-6">
                <a href={`${PM_BASE_URL}/documents`} className={`transition-colors ${useDarkSurfaceHeader ? 'hover:text-white' : 'hover:text-black'}`}>云作引擎</a>
            </div>
          </div>

          {isLoggedIn ? (
            <div className="flex items-center gap-2">
              {/* 功能引导 — replays the onboarding role chooser on demand.
                  Sits just left of the 发布 button (desktop only). */}
              <button
                onClick={() => window.dispatchEvent(new Event('open-onboarding-chooser'))}
                className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3.5 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
                aria-label="功能引导"
              >
                <HelpCircle className="h-4 w-4" /> 功能引导
              </button>

              {/* Publisher takes considerable width with its label; hide on
                  mobile and surface inside the hamburger menu instead. */}
              <div className="hidden md:block">
                <Publisher
                  isLoggedIn={isLoggedIn}
                  userInfo={userInfo}
                  onRequireAuth={onRequireAuth}
                />
              </div>

              <span data-tour="notifications"><NotificationBell /></span>

              {/* Desktop: rich hover menu. Mobile: simple icon link to /messages
                  since hover doesn't exist on touch devices. */}
              <div className="hidden md:block" data-tour="messages">
                <MessageHoverMenu />
              </div>
              <Link
                to="/messages"
                className="md:hidden relative inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-700 hover:bg-gray-100"
                aria-label="消息"
              >
                <MessageSquare className={`h-5 w-5 ${unreadMsgCount > 0 ? 'animate-attention-pulse text-blue-600' : ''}`} />
                {unreadMsgCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-medium flex items-center justify-center">
                    {unreadMsgCount > 99 ? '99+' : unreadMsgCount}
                  </span>
                )}
              </Link>

              <div className="relative group/avatar py-2" ref={menuRef} data-tour="account">
                <button
                  className="h-9 w-9 overflow-hidden rounded-full border-2 border-white shadow-md transition-all group-hover/avatar:scale-105 group-hover/avatar:shadow-lg"
                  onClick={() => setShowUserMenu(s => !s)}
                  aria-label="用户菜单"
                >
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt="用户头像"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-500 to-sky-600">
                      <User className="h-5 w-5 text-white" />
                    </div>
                  )}
                </button>

                {/* Desktop: hover-revealed. Mobile: tap-revealed (controlled
                    by showUserMenu state, click-outside collapses it). */}
                <div className={`absolute right-0 md:right-auto md:left-1/2 top-full mt-0 w-44 md:w-40 md:-translate-x-1/2 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl z-50 transition-all duration-200 ${showUserMenu ? 'opacity-100 visible' : 'opacity-0 invisible'} md:opacity-0 md:invisible md:group-hover/avatar:opacity-100 md:group-hover/avatar:visible`}>
                  <div className="py-2">
                    {menuItems.map((item, index) => (
                      <Link
                        key={index}
                        to={item.path}
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                      >
                        <item.icon className="h-4 w-4 text-gray-500" />
                        <span>{item.label}</span>
                      </Link>
                    ))}

                    <div className="my-1 border-t border-gray-100"></div>

                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center gap-3 px-4 py-3 text-sm text-red-600 transition-colors hover:bg-red-50"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>退出登陆</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Hamburger toggle — mobile only, opens the nav-links panel
                  (找创作者 / 浏览需求 / 关于我们 + Publisher). */}
              <button
                className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-700 hover:bg-gray-100"
                onClick={() => setShowMobileMenu(s => !s)}
                aria-label="菜单"
              >
                {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={onRequireAuth} className="rounded-full bg-black px-4 sm:px-6 py-2 text-sm font-medium text-white transition-all hover:bg-gray-800 shadow-sm">
                登录
              </button>
              <button
                className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-700 hover:bg-gray-100"
                onClick={() => setShowMobileMenu(s => !s)}
                aria-label="菜单"
              >
                {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          )}
        </div>

        {/* Mobile slide-down nav. Sized like a sheet under the header,
            shown only when hamburger is toggled. Closes on link tap. */}
        {showMobileMenu && (
          <div className="md:hidden border-t border-gray-100 bg-white shadow-lg">
            <div className="px-6 py-4 flex flex-col gap-1">
              <Link
                to="/discover?tab=creators"
                onClick={() => setShowMobileMenu(false)}
                className="px-3 py-3 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-50"
              >
                找创作者
              </Link>
              <Link
                to="/discover?tab=briefs"
                onClick={() => setShowMobileMenu(false)}
                className="px-3 py-3 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-50"
              >
                浏览需求
              </Link>
              <Link
                to="/about"
                onClick={() => setShowMobileMenu(false)}
                className="px-3 py-3 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-50"
              >
                关于我们
              </Link>
              <Link
                to={OFFICIAL_STUDIO_PROFILE_PATH}
                onClick={() => setShowMobileMenu(false)}
                className="px-3 py-3 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-50"
              >
                云作Studio
              </Link>
              <a
                href={`${PM_BASE_URL}/documents`}
                onClick={() => setShowMobileMenu(false)}
                className="px-3 py-3 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-50"
              >
                云作引擎
              </a>
              {isLoggedIn && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  {/* Publisher rendered here on mobile so users can still
                      publish without the desktop right-rail entry point. */}
                  <Publisher
                    isLoggedIn={isLoggedIn}
                    userInfo={userInfo}
                    onRequireAuth={onRequireAuth}
                  />
                  <button
                    onClick={() => { setShowMobileMenu(false); window.dispatchEvent(new Event('open-onboarding-chooser')); }}
                    className="mt-1 w-full flex items-center gap-2 px-3 py-3 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <HelpCircle className="h-5 w-5 text-gray-500" /> 功能引导
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Header;
